import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { LoadingState, User } from '@types'
import { TrackingRows } from '@types'

interface analystCasesState {
  loadingStatus: LoadingState
  message: string
  cases: User[]
  analysts: Partial<User>[]
}

const initialState: analystCasesState = {
  loadingStatus: 'NOT_STARTED',
  message: '',
  cases: [],
  analysts: []
}

const casesSlice = createSlice({
  name: 'analystCases',
  initialState,

  reducers: {
    getAllAnalyst: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getAnalystsSuccess: (state, action: PayloadAction<User[]>) => {
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        analysts: action.payload
      }
    },
    getAnalystsFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        analysts: [],
        message: action.payload
      }
    },
    getAllCases: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getCasesByAnalyst: (state, _action: PayloadAction<{ userId: string }>) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getCasesByAnalystSuccess: (state, action: PayloadAction<User[]>) => {
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        cases: action.payload
      }
    },
    getCasesByAnalystFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        cases: [],
        message: action.payload
      }
    },
    clearAnalystCasesState: _state => {
      return initialState
    }
  }
})

export const casesLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.analystCases.loadingStatus, (state: GlobalState) => state.analystCases.message],
  (loadingStatus, message) => {
    const isLoading = loadingStatus === 'LOADING'
    const isFailed = loadingStatus === 'FAILED'
    const isSuccess = loadingStatus === 'SUCCESS'
    return { isLoading, isFailed, isSuccess, message }
  }
)

export const selectCases = createDraftSafeSelector([(state: GlobalState) => state.analystCases.cases], cases => {
  const rows: TrackingRows[] = cases.map(person => ({
    ...person,
    id: person._id,
    parentName: person.parentName ? person.parentName : '',
    bankName: person.bank?.toUpperCase(),
    studyDate: person.stages?.studyDate || '',
    office: person.stages?.office || '',
    bank: person.stages?.bank || '',
    bankDocument: person.bankDocument ?? '',
    success: person.stages?.success || '',
    feesOrPayments: person.stages?.feesOrPayments || '',
    comments: person.comments || '',
    isRejected: person?.isRejected || false,
    createdAt: person.createdAt || '2020',
    fee: person.stages?.fee ?? 0,
    payments: person.stages?.payments ?? 0,
    received: person.stages?.received ?? '',
    returned: person.stages?.returned ?? '',
    rejected: person.stages?.rejected ?? '',
    delivered: person.stages?.delivered ?? ''
  }))
  return rows
})

export const selectAnalystList = createDraftSafeSelector(
  [(state: GlobalState) => state.analystCases.analysts],
  analysts => analysts
)

export const {
  getAllAnalyst,
  getAnalystsSuccess,
  getAnalystsFailed,
  getCasesByAnalyst,
  getAllCases,
  getCasesByAnalystSuccess,
  getCasesByAnalystFailed,
  clearAnalystCasesState
} = casesSlice.actions
export default casesSlice.reducer
